/**
 * The Procurement module's route table.
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads them in. Keeping them here means the paths, the
 * permissions and the components that implement them are decided in one place by
 * whoever owns the module.
 *
 * Two ordering rules are load-bearing:
 *
 *  - `/…/new` is declared **before** `/…/:id(\d+)` everywhere, so `new` can never
 *    be parsed as a document id. The `\d+` constraint makes that safe anyway; the
 *    ordering makes it obvious.
 *  - The receipts and matches "new" routes carry their subject in the query
 *    (`?poId=`, `?invoiceId=`) rather than the path. Receiving is always *against
 *    an order* and matching is always *against an order and an invoice*, and a
 *    query keeps the screen reachable with neither chosen yet — which is what
 *    happens when somebody arrives from the nav rather than from a document.
 *
 * **Every route names one permission, and it is the permission the server guards
 * the screen's primary read with.** Not the write: a route gated on
 * `procurement.po.create` would hide the order list from everyone who may only
 * read orders, and a route gated on nothing at all would render a screen whose
 * every request comes back 403. The write permissions are checked inside the
 * screens, per action, because §6 makes create / approve / receive a
 * segregation-of-duties set and no action may be inferred from another.
 */

import type { RouteRecordRaw } from 'vue-router'

import { REQUISITION_CONTROL_ROUTES } from '@/modules/procurement/category-policies/routes'
import { EVALUATION_ROUTES } from '@/modules/procurement/evaluation/routes'
import { PROCUREMENT_PERMISSIONS } from '@/modules/procurement/permissions'
import { AWARD_ROUTES } from '@/modules/procurement/rfq/award/routes'
import { COMPARISON_ROUTES } from '@/modules/procurement/rfq/evaluation/routes'
import { PROCUREMENT_C7_ROUTES } from '@/modules/procurement/routesC7'

export const PROCUREMENT_ROUTES: RouteRecordRaw[] = [
  /* ------------------------------------------------ purchase requisitions */
  {
    path: '/procurement/requisitions',
    name: 'procurement-requisitions',
    component: () => import('@/modules/procurement/RequisitionsListView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.requisitions',
      permission: PROCUREMENT_PERMISSIONS.prRead,
    },
  },
  {
    path: '/procurement/requisitions/new',
    name: 'procurement-requisition-new',
    component: () => import('@/modules/procurement/RequisitionView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.requisitionNew',
      permission: PROCUREMENT_PERMISSIONS.prCreate,
    },
  },
  {
    path: '/procurement/requisitions/:id(\\d+)',
    name: 'procurement-requisition',
    component: () => import('@/modules/procurement/RequisitionView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.requisition',
      permission: PROCUREMENT_PERMISSIONS.prRead,
    },
  },

  /* -------------------------------------------------------------- RFQs */
  {
    path: '/procurement/rfqs',
    name: 'procurement-rfqs',
    component: () => import('@/modules/procurement/RfqsListView.vue'),
    meta: { titleKey: 'procurement.routeTitles.rfqs', permission: PROCUREMENT_PERMISSIONS.rfqRead },
  },
  {
    path: '/procurement/rfqs/new',
    name: 'procurement-rfq-new',
    component: () => import('@/modules/procurement/RfqView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.rfqNew',
      permission: PROCUREMENT_PERMISSIONS.rfqCreate,
    },
  },
  {
    path: '/procurement/rfqs/:id(\\d+)',
    name: 'procurement-rfq',
    component: () => import('@/modules/procurement/RfqView.vue'),
    meta: { titleKey: 'procurement.routeTitles.rfq', permission: PROCUREMENT_PERMISSIONS.rfqRead },
  },

  /* ---------------------------------------------------- purchase orders */
  {
    path: '/procurement/orders',
    name: 'procurement-orders',
    component: () => import('@/modules/procurement/OrdersListView.vue'),
    meta: { titleKey: 'procurement.routeTitles.orders', permission: PROCUREMENT_PERMISSIONS.poRead },
  },
  {
    path: '/procurement/orders/new',
    name: 'procurement-order-new',
    component: () => import('@/modules/procurement/OrderView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.orderNew',
      permission: PROCUREMENT_PERMISSIONS.poCreate,
    },
  },
  {
    path: '/procurement/orders/:id(\\d+)',
    name: 'procurement-order',
    component: () => import('@/modules/procurement/OrderView.vue'),
    meta: { titleKey: 'procurement.routeTitles.order', permission: PROCUREMENT_PERMISSIONS.poRead },
  },

  /* ----------------------------------------------------- goods receipts */
  {
    path: '/procurement/receipts',
    name: 'procurement-receipts',
    component: () => import('@/modules/procurement/ReceiptsListView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.receipts',
      permission: PROCUREMENT_PERMISSIONS.grnRead,
    },
  },
  {
    path: '/procurement/receipts/new',
    name: 'procurement-receipt-new',
    component: () => import('@/modules/procurement/ReceiveView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.receiptNew',
      permission: PROCUREMENT_PERMISSIONS.grnCreate,
    },
  },
  {
    path: '/procurement/receipts/:id(\\d+)',
    name: 'procurement-receipt',
    component: () => import('@/modules/procurement/ReceiptView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.receipt',
      permission: PROCUREMENT_PERMISSIONS.grnRead,
    },
  },

  /* ---------------------------------------------------- three-way match */
  {
    path: '/procurement/matches',
    name: 'procurement-matches',
    component: () => import('@/modules/procurement/MatchesListView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.matches',
      permission: PROCUREMENT_PERMISSIONS.matchRead,
    },
  },
  {
    path: '/procurement/matches/new',
    name: 'procurement-match-new',
    component: () => import('@/modules/procurement/MatchView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.matchNew',
      permission: PROCUREMENT_PERMISSIONS.matchCreate,
    },
  },
  {
    path: '/procurement/matches/:id(\\d+)',
    name: 'procurement-match',
    component: () => import('@/modules/procurement/MatchView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.match',
      permission: PROCUREMENT_PERMISSIONS.matchRead,
    },
  },

  /* --------------------------------------------------------- reports */
  {
    path: '/procurement/reports/gr-ir-aging',
    name: 'procurement-gr-ir-aging',
    component: () => import('@/modules/procurement/GrIrAgingView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.grIrAging',
      permission: PROCUREMENT_PERMISSIONS.reportRead,
    },
  },
  {
    path: '/procurement/vendor-scorecards',
    name: 'procurement-vendor-scorecards',
    component: () => import('@/modules/procurement/ScorecardsView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.scorecards',
      permission: PROCUREMENT_PERMISSIONS.scorecardRead,
    },
  },
  {
    path: '/procurement/settings',
    name: 'procurement-settings',
    component: () => import('@/modules/procurement/SettingsView.vue'),
    meta: {
      titleKey: 'procurement.routeTitles.settings',
      permission: PROCUREMENT_PERMISSIONS.settingsManage,
    },
  },

  /* C7's additions (§5.5) live in their own file so a milestone's routes are
     reviewable as a block; the router still imports one array. */
  ...PROCUREMENT_C7_ROUTES,

  /* P45 (FSD Part 4): requisition controls (4.2.1), supplier evaluation and its reports (4.2.2), the
     quotation comparison (4.2.4) and awards (4.2.4). Each feature keeps its own records, with literal paths. */
  ...REQUISITION_CONTROL_ROUTES,
  ...EVALUATION_ROUTES,
  ...COMPARISON_ROUTES,
  ...AWARD_ROUTES,
]
